import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user just registered - only show for new registrations
    const justRegistered = localStorage.getItem("viraly_just_registered");
    const existingConsent = localStorage.getItem("viraly_cookie_consent");
    
    // Only show if: just registered AND no existing consent
    if (justRegistered && !existingConsent) {
      const timer = setTimeout(() => {
        setShowBanner(true);
        // Clear the registration flag after showing
        localStorage.removeItem("viraly_just_registered");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = { necessary: true, analytics: true, marketing: true, timestamp: Date.now() };
    localStorage.setItem("viraly_cookie_consent", JSON.stringify(allConsent));
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const minimalConsent = { necessary: true, analytics: false, marketing: false, timestamp: Date.now() };
    localStorage.setItem("viraly_cookie_consent", JSON.stringify(minimalConsent));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const customConsent = { ...preferences, timestamp: Date.now() };
    localStorage.setItem("viraly_cookie_consent", JSON.stringify(customConsent));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-viraly-card border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            {!showSettings ? (
              // Main Banner
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-viraly-orange/20 flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-viraly-orange" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-lg mb-2">We value your privacy</h3>
                    <p className="text-sm text-viraly-muted mb-4">
                      We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                      By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or 
                      learn more in our{" "}
                      <Link to="/privacy" className="text-viraly-orange hover:underline">Privacy Policy</Link>.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        onClick={handleAcceptAll}
                        className="btn-primary px-6"
                        data-testid="cookie-accept-all"
                      >
                        Accept All
                      </Button>
                      <Button
                        onClick={handleAcceptNecessary}
                        variant="outline"
                        className="btn-secondary px-6"
                        data-testid="cookie-accept-necessary"
                      >
                        Necessary Only
                      </Button>
                      <Button
                        onClick={() => setShowSettings(true)}
                        variant="ghost"
                        className="text-viraly-muted hover:text-white flex items-center gap-2"
                        data-testid="cookie-settings-btn"
                      >
                        <Settings className="w-4 h-4" />
                        Manage Preferences
                      </Button>
                    </div>
                  </div>
                  <button
                    onClick={handleAcceptNecessary}
                    className="text-viraly-muted hover:text-white p-1"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              // Settings Panel
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-bold text-lg">Cookie Preferences</h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-viraly-muted hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-viraly-success/20 flex items-center justify-center">
                          <Check className="w-4 h-4 text-viraly-success" />
                        </div>
                        <span className="font-medium">Necessary Cookies</span>
                      </div>
                      <span className="text-xs text-viraly-success bg-viraly-success/20 px-2 py-1 rounded">Always Active</span>
                    </div>
                    <p className="text-sm text-viraly-muted pl-11">
                      Essential for the website to function properly. These cannot be disabled.
                    </p>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                            preferences.analytics ? 'bg-viraly-orange' : 'bg-white/10'
                          }`}
                        >
                          {preferences.analytics && <Check className="w-4 h-4 text-white" />}
                        </button>
                        <span className="font-medium">Analytics Cookies</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        preferences.analytics ? 'text-viraly-success bg-viraly-success/20' : 'text-viraly-muted bg-white/10'
                      }`}>
                        {preferences.analytics ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <p className="text-sm text-viraly-muted pl-11">
                      Help us understand how visitors interact with our website to improve user experience.
                    </p>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                            preferences.marketing ? 'bg-viraly-orange' : 'bg-white/10'
                          }`}
                        >
                          {preferences.marketing && <Check className="w-4 h-4 text-white" />}
                        </button>
                        <span className="font-medium">Marketing Cookies</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        preferences.marketing ? 'text-viraly-success bg-viraly-success/20' : 'text-viraly-muted bg-white/10'
                      }`}>
                        {preferences.marketing ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <p className="text-sm text-viraly-muted pl-11">
                      Used to deliver personalized advertisements and measure their effectiveness.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3">
                  <Button
                    onClick={() => setShowSettings(false)}
                    variant="ghost"
                    className="text-viraly-muted hover:text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSavePreferences}
                    className="btn-primary px-6"
                    data-testid="cookie-save-preferences"
                  >
                    Save Preferences
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
